import React from "react";
import { Card, Row, Col, Statistic, Progress, message } from "antd";
import { 
  ArrowUpOutlined, 
  ArrowDownOutlined,
  EyeOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  UserOutlined
} from "@ant-design/icons";
import MobilePullToRefresh from "./MobilePullToRefresh";

const MobileDashboard = ({ data, title, onRefresh }) => {
  const handleRefresh = async () => {
    // Simulate refresh delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    if (onRefresh) {
      await onRefresh();
    }
    message.success('ข้อมูลได้รับการอัปเดตแล้ว');
  };
  // Sample mobile-optimized dashboard data
  const mobileStats = [
    {
      title: "วันนี้",
      value: data?.today || 0,
      icon: <EyeOutlined />,
      color: "#1890ff",
      trend: "up",
      trendValue: "12%"
    },
    {
      title: "รักษาความปลอดภัย",
      value: data?.security || 0,
      icon: <SafetyOutlined />,
      color: "#52c41a",
      trend: "up", 
      trendValue: "8%"
    },
    {
      title: "เวลาเฉลี่ย",
      value: data?.avgTime || "0 นาที",
      icon: <ClockCircleOutlined />,
      color: "#fa8c16",
      trend: "down",
      trendValue: "3%"
    },
    {
      title: "ผู้ใช้งาน",
      value: data?.users || 0,
      icon: <UserOutlined />,
      color: "#722ed1",
      trend: "up",
      trendValue: "15%"
    }
  ];

  return (
    <MobilePullToRefresh onRefresh={handleRefresh}>
      <div className="mobile-dashboard p-4 pb-20">
      {/* Mobile Header */}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800 mb-1">{title || "ภาพรวมระบบ"}</h2>
        <p className="text-sm text-gray-500">อัพเดทล่าสุด: {new Date().toLocaleTimeString('th-TH')}</p>
      </div>

      {/* Mobile Stats Grid - 2x2 layout */}
      <Row gutter={[8, 8]} className="mb-4">
        {mobileStats.map((stat, index) => (
          <Col span={12} key={index}>
            <Card 
              className="mobile-stat-card"
              bodyStyle={{ padding: '12px' }}
              style={{
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.98)',
                border: `1px solid ${stat.color}20`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                  style={{ backgroundColor: stat.color }}
                >
                  {stat.icon}
                </div>
                <div className={`flex items-center text-xs ${
                  stat.trend === 'up' ? 'text-green-500' : 'text-red-500'
                }`}>
                  {stat.trend === 'up' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  <span className="ml-1">{stat.trendValue}</span>
                </div>
              </div>
              <div>
                <div className="text-lg font-bold text-gray-800 mb-1">
                  {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {stat.title}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Mobile Progress Section */}
      <Card 
        title={
          <div className="flex items-center">
            <div className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center text-white text-sm mr-2">
              📊
            </div>
            <span className="text-sm font-medium">ความคืบหน้าวันนี้</span>
          </div>
        }
        className="mb-4"
        bodyStyle={{ padding: '12px' }}
        style={{
          borderRadius: '12px',
          background: 'rgba(255, 255, 255, 0.98)'
        }}
      >
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span>เป้าหมายรายวัน</span>
              <span>75%</span>
            </div>
            <Progress 
              percent={75} 
              size="small" 
              strokeColor="#52c41a"
              showInfo={false}
            />
          </div>
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span>คุณภาพงาน</span>
              <span>92%</span>
            </div>
            <Progress 
              percent={92} 
              size="small" 
              strokeColor="#1890ff"
              showInfo={false}
            />
          </div>
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span>ความปลอดภัย</span>
              <span>88%</span>
            </div>
            <Progress 
              percent={88} 
              size="small" 
              strokeColor="#fa8c16"
              showInfo={false}
            />
          </div>
        </div>
      </Card>

      {/* Mobile Quick Actions */}
      <Card 
        title={
          <div className="flex items-center">
            <div className="w-6 h-6 bg-purple-500 rounded-lg flex items-center justify-center text-white text-sm mr-2">
              ⚡
            </div>
            <span className="text-sm font-medium">การดำเนินการด่วน</span>
          </div>
        }
        bodyStyle={{ padding: '12px' }}
        style={{
          borderRadius: '12px',
          background: 'rgba(255, 255, 255, 0.98)'
        }}
      >
        <div className="grid grid-cols-2 gap-2">
          <button className="flex flex-col items-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white mb-1">
              📹
            </div>
            <span className="text-xs font-medium text-blue-700">ดูกล้อง</span>
          </button>
          <button className="flex flex-col items-center p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center text-white mb-1">
              🚶‍♂️
            </div>
            <span className="text-xs font-medium text-green-700">ตรวจตรา</span>
          </button>
          <button className="flex flex-col items-center p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white mb-1">
              📊
            </div>
            <span className="text-xs font-medium text-orange-700">รายงาน</span>
          </button>
          <button className="flex flex-col items-center p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
            <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center text-white mb-1">
              ⚙️
            </div>
            <span className="text-xs font-medium text-purple-700">ตั้งค่า</span>
          </button>
        </div>
      </Card>
      </div>
    </MobilePullToRefresh>
  );
};

export default MobileDashboard;